import { __assign, __read } from "tslib";
import { useAdminAcquiredApps } from '@office-start/admin-acquired-apps-service';
import { useAppBarUserData } from '@office-start/data-resolver-app-pinning';
import { useHarmonyTheme } from '@office-start/visual-data-service';
import { useProvisionedWorkloads } from '@office-start/workloads-service';
import { useEffect, useMemo } from 'react';
import create from 'zustand';
import { useTeachingUIGetUserData } from './useTeachingUIGetUserData';
var getDeferredPromiseWithTimeout = function (timeoutInMs) {
    if (timeoutInMs === void 0) { timeoutInMs = 10000; }
    var deferredPromise = {};
    var promise = new Promise(function (resolve, reject) {
        deferredPromise.resolve = resolve;
        deferredPromise.reject = reject;
    });
    deferredPromise.promise = Promise.race([
        promise,
        new Promise(function (_, reject) {
            var timeoutId = setTimeout(function () {
                reject('Timed out');
            }, timeoutInMs);
            promise.then(function () {
                clearTimeout(timeoutId);
            });
        }),
    ]);
    return deferredPromise;
};
var useExtraActivationDataStore = create(function (_, get) { return ({
    data: {
        userProfileInfoPromise: getDeferredPromiseWithTimeout(),
        appBarUserDataPromise: getDeferredPromiseWithTimeout(),
        adminAcquiredAppsPromise: getDeferredPromiseWithTimeout(),
        themePromise: getDeferredPromiseWithTimeout(),
        workloadsPromise: getDeferredPromiseWithTimeout(),
    },
    resolve: function (property, result) {
        var _a;
        var data = get().data;
        if (data[property]) {
            (_a = data[property]) === null || _a === void 0 ? void 0 : _a.resolve(result);
        }
    },
}); });
export var useResolveDeferredPromise = function () {
    return useExtraActivationDataStore(function (state) { return state.resolve; });
};
export var useExtraActivationDataState = function () {
    return useExtraActivationDataStore(function (state) { return state.data; });
};
var useDeferredPromise = function (key, result) {
    var resolve = useResolveDeferredPromise();
    useEffect(function () {
        if (result) {
            resolve(key, result);
        }
    }, [result, key, resolve]);
};
/**
 * Hook to get all the extra data that might be needed for sequence validation
 * @returns Object containing promises for all the extra data that might be
 *  needed for sequence validation
 */
export var useExtraActivationData = function () {
    var data = useExtraActivationDataState();
    return useMemo(function () {
        return Object.keys(data).reduce(function (acc, key) {
            var _a;
            return (__assign(__assign({}, acc), (_a = {}, _a[key] = data[key].promise, _a)));
        }, {});
    }, [data]);
};
export var useSetupExtraActivationData = function () {
    var userProfileInfo = useTeachingUIGetUserData();
    useDeferredPromise('userProfileInfoPromise', userProfileInfo);
    var adminAcquiredApps = useAdminAcquiredApps().adminAcquiredApps;
    useDeferredPromise('adminAcquiredAppsPromise', adminAcquiredApps);
    var _a = __read(useHarmonyTheme(), 1), theme = _a[0];
    useDeferredPromise('themePromise', theme);
    var _b = __read(useAppBarUserData(), 1), appBarUserData = _b[0];
    useDeferredPromise('appBarUserDataPromise', appBarUserData);
    var workloads = useProvisionedWorkloads();
    useDeferredPromise('workloadsPromise', workloads);
};
//# sourceMappingURL=useExtraActivationData.js.map